import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, Button, Rating, TextField, Typography } from "@mui/material";

const panelSx = {
  border: "1px solid rgba(242, 237, 228, 0.12)",
  borderRadius: "16px",
  background: "rgba(0, 0, 0, 0.35)",
  boxShadow: "0px 4px 50px 3px rgba(242, 237, 228, 0.06)",
};

const InlineReviewForm = ({ token }) => {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    setRating(null);
    setComment("");
    setStatus("idle");
    setError("");
  }, [token]);

  const handleSubmit = async () => {
    if (!token || !rating || status === "submitting") return;

    setStatus("submitting");
    setError("");

    try {
      await axios.post("/api/reviews/submit", {
        token,
        story: rating,
        puzzles: rating,
        difficulty: rating,
        route: rating,
        overall: rating,
        comment,
      });
      setStatus("submitted");
    } catch (submitError) {
      console.error("Nepodařilo se odeslat recenzi:", submitError);
      setError("Recenzi se nepodařilo odeslat. Zkuste to prosím znovu.");
      setStatus("idle");
    }
  };

  return (
    <Box sx={{ ...panelSx, width: "100%", p: 2.2 }}>
      <Typography
        sx={{
          fontFamily: "'Cinzel', serif",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontSize: 13,
          color: "rgba(242,237,228,0.75)",
          textAlign: "center",
          mb: 0.8,
        }}
      >
        Recenze
      </Typography>

      {status === "submitted" ? (
        <Typography
          role="status"
          sx={{
            color: "#F2EDE4",
            fontFamily: "'Cactus Classical Serif', serif",
            fontSize: 14,
            lineHeight: 1.7,
            textAlign: "center",
          }}
        >
          Děkujeme. Recenze byla odeslána.
        </Typography>
      ) : (
        <>
          <Typography
            sx={{
              color: "rgba(242,237,228,0.72)",
              fontFamily: "'Cactus Classical Serif', serif",
              fontSize: 14,
              textAlign: "center",
              mb: 1.2,
            }}
          >
            Jak se vám hra líbila?
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Rating
              name="inline-game-review"
              value={rating}
              max={5}
              disabled={status === "submitting"}
              onChange={(_, value) => {
                if (value !== null) setRating(value);
              }}
              sx={{
                fontSize: { xs: "2.35rem", sm: "2.65rem" },
                "& .MuiRating-iconEmpty": { color: "rgba(242,237,228,0.22)" },
                "& .MuiRating-iconFilled": { color: "rgba(242,237,228,0.9)" },
                "& .MuiRating-iconHover": { color: "#F2EDE4" },
              }}
            />
          </Box>

          {rating !== null && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Komentář (volitelný)"
                value={comment}
                disabled={status === "submitting"}
                onChange={(event) => setComment(event.target.value)}
                inputProps={{ maxLength: 1000 }}
                sx={{
                  "& label": {
                    color: "rgba(242,237,228,0.55)",
                    fontFamily: "'Cactus Classical Serif', serif",
                  },
                  "& label.Mui-focused": { color: "rgba(242,237,228,0.72)" },
                  "& .MuiOutlinedInput-root": {
                    color: "#F2EDE4",
                    fontFamily: "'Cactus Classical Serif', serif",
                    background: "rgba(0,0,0,0.28)",
                    borderRadius: "8px",
                    "& fieldset": { borderColor: "rgba(242,237,228,0.14)" },
                    "&:hover fieldset": { borderColor: "rgba(242,237,228,0.24)" },
                    "&.Mui-focused fieldset": { borderColor: "rgba(242,237,228,0.34)" },
                  },
                }}
              />

              {error && (
                <Typography
                  role="alert"
                  sx={{
                    mt: 1.2,
                    color: "rgba(255,130,130,0.9)",
                    fontFamily: "'Cactus Classical Serif', serif",
                    fontSize: 13,
                    textAlign: "center",
                  }}
                >
                  {error}
                </Typography>
              )}

              <Button
                fullWidth
                variant="contained"
                disabled={status === "submitting"}
                onClick={handleSubmit}
                sx={{
                  mt: 1.6,
                  minHeight: 48,
                  borderRadius: "8px",
                  background: "#F2EDE4",
                  color: "#151515",
                  boxShadow: "none",
                  fontFamily: "'Cinzel', serif",
                  fontSize: 13,
                  letterSpacing: "0.08em",
                  "&:hover": { background: "#FFFFFF", boxShadow: "none" },
                  "&.Mui-disabled": {
                    background: "rgba(242,237,228,0.35)",
                    color: "rgba(21,21,21,0.65)",
                  },
                }}
              >
                {status === "submitting" ? "Odesílám…" : "Odeslat recenzi"}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default InlineReviewForm;
